import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ProductCard from '../components/ProductCard'
import { Loader, EmptyState, Pagination } from '../components/Loader'
import { productsApi } from '../services/api'
import { Product, ProductType, ProductCategory } from '../types'

const TYPE_FILTERS: Array<{ value: ProductType | ''; label: string }> = [
  { value: '', label: 'همه' },
  { value: 'GOLD', label: 'طلا' },
  { value: 'SILVER', label: 'نقره' }
]

const CATEGORY_FILTERS: Array<{ value: ProductCategory | ''; label: string }> = [
  { value: '', label: 'همه دسته‌ها' },
  { value: 'SHMESH', label: 'شمش' },
  { value: 'ABSHADE', label: 'آب‌شده' },
  { value: 'COIN', label: 'سکه' },
  { value: 'BAR', label: 'بار' }
]

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'sortOrder:asc', label: 'پیش‌فرض' },
  { value: 'sellingPrice:asc', label: 'ارزان‌ترین' },
  { value: 'sellingPrice:desc', label: 'گران‌ترین' },
  { value: 'name:asc', label: 'نام' }
]

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const type = (searchParams.get('type') as ProductType) || ''
  const category = (searchParams.get('category') as ProductCategory) || ''
  const search = searchParams.get('search') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const sort = searchParams.get('sort') || 'sortOrder:asc'

  const [products, setProducts] = useState<Product[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState(search)

  const updateParams = useCallback(
    (next: Record<string, string>) => {
      const merged = new URLSearchParams(searchParams.toString())
      Object.entries(next).forEach(([k, v]) => {
        if (v) merged.set(k, v)
        else merged.delete(k)
      })
      merged.delete('page')
      setSearchParams(merged)
    },
    [searchParams, setSearchParams]
  )

  useEffect(() => {
    setLoading(true)
    const [sortBy, sortOrder] = sort.split(':') as [string, 'asc' | 'desc']
    productsApi
      .getAll({ type: type || undefined, category: category || undefined, search: search || undefined, page, limit: 12, sortBy, sortOrder })
      .then((res) => {
        if (res.data.success && res.data.data) {
          setProducts(res.data.data.products)
          setTotal(res.data.data.pagination.total)
          setTotalPages(res.data.data.pagination.totalPages)
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false))
  }, [type, category, search, page, sort])

  const submitSearch = () => {
    updateParams({ search: searchInput })
  }

  return (
    <div className="container-main py-10">
      <h1 className="text-2xl font-black text-gray-900 mb-1">محصولات خرید و فروش</h1>
      <p className="text-sm text-gray-500 mb-6">شمش، آب‌شده، سکه و بار طلا و نقره با قیمت لحظه‌ای</p>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <input
          className="input flex-1 min-w-[160px] sm:max-w-md"
          placeholder="جستجوی محصول…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
        />
        <button className="btn btn-primary shrink-0" onClick={submitSearch}>جستجو</button>
        {search && (
          <button className="btn btn-secondary shrink-0" onClick={() => { setSearchInput(''); updateParams({ search: '' }) }}>
            حذف جستجو
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-8">
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value || 'all-type'}
              onClick={() => updateParams({ type: f.value })}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${type === f.value ? 'bg-white shadow text-primary-700 font-bold' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <select
          className="input !w-auto"
          value={category}
          onChange={(e) => updateParams({ category: e.target.value })}
        >
          {CATEGORY_FILTERS.map((c) => (
            <option key={c.value || 'all-cat'} value={c.value}>{c.label}</option>
          ))}
        </select>

        <select
          className="input !w-auto"
          value={sort}
          onChange={(e) => updateParams({ sort: e.target.value })}
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader /></div>
      ) : products.length === 0 ? (
        <EmptyState title="محصولی یافت نشد" description="فیلترها را تغییر دهید یا بعداً دوباره تلاش کنید." />
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-4">{total.toLocaleString('fa-IR')} محصول</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={(p) => updateParams({ page: String(p) })} />
        </>
      )}
    </div>
  )
}

export default Products